import { useQuery } from '@apollo/client/react';
import { Eye, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Offers_Status_Enum } from '@graphql/__generated__/graphql';
import {
  type CardHeaderIconAction,
  CardHeaderIconActions,
} from '@modules/_shared/components/CardHeaderIconActions';
import {
  CountActionCard,
  CountActionCardSkeleton,
} from '@modules/_shared/components/CountActionCard';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { GET_LISTING_OFFERS_QUERY } from '@modules/listings/ListingOffers';
import { useLocale } from '@shared/hooks/locale';

interface ListingOffersCardProps {
  listingId?: string | null;
  openedDefault?: boolean;
  readOnly?: boolean;
}

export const ListingOffersCardSkeleton: React.FC<{
  openedDefault?: boolean;
}> = ({ openedDefault }) => {
  const { t } = useLocale();
  return (
    <ListingAccordion title={t('Offers')} defaultExpanded={openedDefault}>
      <CountActionCardSkeleton
        countLabels={[t('Pending'), t('Refused'), t('Accepted')]}
      />
    </ListingAccordion>
  );
};

export const ListingOffersCard: React.FC<ListingOffersCardProps> = ({
  listingId,
  openedDefault = false,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading } = useQuery(GET_LISTING_OFFERS_QUERY, {
    variables: { listingId: listingId ?? '' },
    skip: listingId == null,
  });

  const acceptedCount =
    data?.offers.filter(({ status }) => status === Offers_Status_Enum.Accepted)
      .length ?? 0;
  const pendingCount =
    data?.offers.filter(({ status }) => status === Offers_Status_Enum.Pending)
      .length ?? 0;
  const refusedCount =
    data?.offers.filter(({ status }) => status === Offers_Status_Enum.Refused)
      .length ?? 0;

  const offersListPath =
    listingId != null ? `/listings/${listingId}/offers` : undefined;
  const offersSearch = searchParams.toString();

  const headerActions: CardHeaderIconAction[] =
    readOnly || offersListPath == null
      ? []
      : [
          {
            label: t('Add offer'),
            icon: <Plus size={16} />,
            onClick: () => {
              const next = new URLSearchParams(searchParams);
              next.set('create_offer', '1');
              setSearchParams(next);
            },
          },
          {
            label: t('View offers'),
            icon: <Eye size={16} />,
            link: { to: offersListPath, search: offersSearch },
          },
        ];

  return (
    <ListingAccordion
      title={t('Offers')}
      defaultExpanded={openedDefault}
      headerAction={
        headerActions.length > 0 ? (
          <CardHeaderIconActions actions={headerActions} />
        ) : undefined
      }
    >
      <CountActionCard
        isLoading={loading || listingId == null}
        countElements={[
          { label: t('Pending'), value: pendingCount },
          {
            label: t('Refused'),
            value: refusedCount,
          },
          { label: t('Accepted'), value: acceptedCount },
        ]}
      />
    </ListingAccordion>
  );
};
